import { FC, useState } from 'react'

import styled from 'styled-components'
import { useMediaQuery } from 'react-responsive'

import Arrow from 'src/Assets/Svg/common/arrowLeft.svg?react'
import { MAX_MOBILE_WIDTH, variables } from 'src/Styled/variables'
import { FullScreenModal } from 'src/Components/FullScreenModal'
import { IconButton } from 'src/Components/Buttons/IconButton'
import { MobileSearchInput } from 'src/Pages/Artist/CreateCampaignPage/Components/TrackSearch/Components/MobileSearchInput'
import CloseIcon from 'src/Assets/Svg/common/cross.svg?react'
import ErrorIcon from 'src/Assets/Svg/common/error.svg?react'
import { Loading } from 'src/Pages/Artist/CreateCampaignPage/Components/TrackSearch/Components/Loading'

import { ArtistItem, ArtistItemData } from '../../ArtistItem'
import { ArtistsScrollList } from '../styles'

const ArtistsContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  align-self: stretch;
  padding: 8px;
  overflow-y: auto;
  max-height: calc(100vh - 8px);
  padding-top: 56px;
`

const StyledIconButton = styled(IconButton)`
  border: none;
`

const MobileInputContainer = styled.div`
  display: flex;
  width: 100%;
  height: 56px;
  padding: 0px 8px;
  align-items: center;
  gap: 4px;
  position: fixed;
  z-index: 1001;
  background-color: ${({ theme }) => theme.color.white};
  border-bottom: 1px solid ${({ theme }) => theme.color.lightGrey};
`

const MobileInputButton = styled.div<{
  $hasValue?: boolean
}>`
  border-radius: 16px;
  padding: 16px;
  border: 1px solid ${({ theme }) => theme.color.lightGrey};
  background: ${({ theme }) => theme.color.white};
  width: 100%;

  font-family: 'Segoe UI';
  font-size: ${variables.fontSize14};
  font-style: normal;
  font-weight: ${variables.fontWeight400};
  line-height: 150%;
  color: ${({ theme, $hasValue }) =>
    $hasValue ? theme.color.darkGrey : theme.color.slateGrey};
`

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  padding: 16px;
  color: ${({ theme }) => theme.color.mediumRed};
`

export interface MobileArtistSearchProps {
  placeholder: string
  searchedArtists?: ArtistItemData[] | null
  selectedArtist?: ArtistItemData
  isLoading?: boolean
  error?: string
  value?: string
  maxLength?: number
  onChange?: (value: string) => void
}

const renderLoadingState = (): JSX.Element => (
  <ArtistsContainer>
    <Loading />
  </ArtistsContainer>
)

const renderErrorState = (message: string): JSX.Element => (
  <ArtistsContainer>
    <ErrorContainer>
      <ErrorIcon />
      <span>{message}</span>
    </ErrorContainer>
  </ArtistsContainer>
)

const renderEmptySearchState = (): JSX.Element => (
  <ArtistsContainer>
    <ErrorContainer>
      <ErrorIcon />
      <span>Artist is not found</span>
    </ErrorContainer>
  </ArtistsContainer>
)

const renderArtistsList = (
  artists: ArtistItemData[],
  onArtistClick: () => void,
): JSX.Element => (
  <ArtistsContainer>
    <ArtistsScrollList>
      {artists.map((artist, index) => (
        <ArtistItem
          key={`${artist.artistName}-${index}`}
          {...artist}
          onClick={() => {
            artist.onClick?.()
            onArtistClick()
          }}
        />
      ))}
    </ArtistsScrollList>
  </ArtistsContainer>
)

const getSearchContent = (
  isLoading: boolean,
  error: string | undefined,
  searchedArtists: ArtistItemData[] | null | undefined,
  value: string | undefined,
  onArtistClick: () => void,
): JSX.Element | null => {
  if (isLoading) {
    return renderLoadingState()
  }

  if (error) {
    return renderErrorState(error)
  }

  const hasResults = searchedArtists && searchedArtists.length > 0
  if (hasResults) {
    return renderArtistsList(searchedArtists, onArtistClick)
  }

  const shouldShowEmpty =
    value && Array.isArray(searchedArtists) && searchedArtists.length === 0
  if (shouldShowEmpty) {
    return renderEmptySearchState()
  }

  return null
}

export const MobileArtistSearch: FC<MobileArtistSearchProps> = ({
  placeholder,
  searchedArtists,
  selectedArtist,
  isLoading,
  error,
  value,
  maxLength,
  onChange,
}) => {
  const [isMobileSearch, setIsMobileSearch] = useState(false)
  const isMobile = useMediaQuery({ maxWidth: MAX_MOBILE_WIDTH })

  if (!isMobile) {
    return null
  }

  const onEnterToMobileSearch = (): void => {
    setIsMobileSearch(true)
  }

  const onMobileBackButtonClick = (): void => {
    setIsMobileSearch(false)
  }

  const searchContent = getSearchContent(
    isLoading ?? false,
    error,
    searchedArtists,
    value,
    () => setIsMobileSearch(false),
  )

  const displayText = selectedArtist?.artistName || placeholder

  return (
    <>
      <MobileInputButton
        $hasValue={Boolean(selectedArtist)}
        onClick={onEnterToMobileSearch}
      >
        {displayText}
      </MobileInputButton>
      <FullScreenModal open={isMobileSearch}>
        <MobileInputContainer>
          <StyledIconButton onClick={onMobileBackButtonClick}>
            <Arrow />
          </StyledIconButton>

          <MobileSearchInput
            placeholder={placeholder}
            allowClear={{ clearIcon: <CloseIcon /> }}
            value={value}
            maxLength={maxLength}
            onChange={(e) => onChange?.(e.target.value)}
          />
        </MobileInputContainer>

        {searchContent}
      </FullScreenModal>
    </>
  )
}
